import React, { useState } from "react";
import { createPortal } from "react-dom";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import SkipNextIcon from "@mui/icons-material/SkipNext";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import HourglassBottomIcon from "@mui/icons-material/HourglassBottom";
import { getLiveTrainingPortalRoot } from "../../hooks/useLiveFullscreen";
import { INTERVAL_PHASES, formatIntervalClock } from "../../utils/intervalTimer";
import { intervalSoundMuted, setIntervalSoundMuted } from "../../utils/intervalBeep";
import "./LiveTrainingCoachChronoPanel.css";
import "./LiveTrainingModeTools.css";

const PRESETS = [
  { key: "tabata", workSec: 20, restSec: 10, rounds: 8 },
  { key: "shadow", workSec: 30, restSec: 15, rounds: 6 },
  { key: "footwork", workSec: 45, restSec: 30, rounds: 5 },
  { key: "stretch", workSec: 30, restSec: 10, rounds: 8 },
];

function Stepper({ label, value, unit, step, min, max, disabled, onChange }) {
  return (
    <div className="lt-interval__stepper">
      <span className="lt-interval__stepper-label">{label}</span>
      <div className="lt-interval__stepper-row">
        <button
          type="button"
          className="lt-chrono-panel__btn"
          onClick={() => onChange(Math.max(min, value - step))}
          disabled={disabled || value <= min}
          aria-label={`${label} -${step}`}
        >
          <RemoveIcon fontSize="small" />
        </button>
        <span className="lt-interval__stepper-value">
          {value}
          {unit ? <small>{unit}</small> : null}
        </span>
        <button
          type="button"
          className="lt-chrono-panel__btn"
          onClick={() => onChange(Math.min(max, value + step))}
          disabled={disabled || value >= max}
          aria-label={`${label} +${step}`}
        >
          <AddIcon fontSize="small" />
        </button>
      </div>
    </div>
  );
}

/** Coach sheet for work / rest interval exercises; state lives in useCoachIntervalTimer. */
export function LiveTrainingIntervalTimerPanel({
  open,
  onClose,
  timer,
  t,
  brandPrimary = "#0877BC",
  brandSecondary = "#46528b",
}) {
  const [muted, setMuted] = useState(() => intervalSoundMuted());
  if (!open || !timer || typeof document === "undefined") return null;

  const { state, remainingSec } = timer;
  const editable = state.phase === INTERVAL_PHASES.IDLE || state.phase === INTERVAL_PHASES.DONE;
  const live = state.phase === INTERVAL_PHASES.WORK || state.phase === INTERVAL_PHASES.REST;
  const phaseTotal = state.phase === INTERVAL_PHASES.REST ? state.restSec : state.workSec;
  const progress = live && phaseTotal > 0 ? 1 - remainingSec / phaseTotal : state.phase === INTERVAL_PHASES.DONE ? 1 : 0;
  const circumference = 2 * Math.PI * 52;
  const totalSec = state.rounds * state.workSec + Math.max(0, state.rounds - 1) * state.restSec;
  const phaseLabel = {
    [INTERVAL_PHASES.IDLE]: t("liveModeIntervalReady", "Ready"),
    [INTERVAL_PHASES.WORK]: t("liveModeIntervalWork", "Work"),
    [INTERVAL_PHASES.REST]: t("liveModeIntervalRest", "Rest"),
    [INTERVAL_PHASES.DONE]: t("liveModeIntervalDone", "Done"),
  }[state.phase];

  const sheet = (
    <div className="lt-chrono-panel__backdrop" role="presentation" onClick={onClose}>
      <div
        className="lt-chrono-panel lt-interval"
        role="dialog"
        aria-modal="true"
        aria-labelledby="lt-interval-title"
        style={{ "--lt-brand": brandPrimary, "--lt-brand-secondary": brandSecondary }}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="lt-chrono-panel__header">
          <div className="lt-chrono-panel__header-text">
            <p className="lt-chrono-panel__eyebrow">{t("liveModeIntervalEyebrow", "Timer exercise")}</p>
            <h2 id="lt-interval-title">
              <HourglassBottomIcon fontSize="small" />
              {t("liveModeIntervalOpen", "Interval timer")}
            </h2>
            <p className="lt-chrono-panel__subtitle">
              {t(
                "liveModeIntervalSubtitle",
                "Players see the same countdown on their phones. A beep marks each change."
              )}
            </p>
          </div>
          <div className="lt-chrono-panel__header-actions">
            <button
              type="button"
              className="lt-chrono-panel__close"
              onClick={() => {
                setIntervalSoundMuted(!muted);
                setMuted(!muted);
              }}
              aria-pressed={muted}
              aria-label={muted ? t("liveModeIntervalSoundOn", "Turn sound on") : t("liveModeIntervalSoundOff", "Mute beeps")}
              title={muted ? t("liveModeIntervalSoundOn", "Turn sound on") : t("liveModeIntervalSoundOff", "Mute beeps")}
            >
              {muted ? <VolumeOffIcon fontSize="small" /> : <VolumeUpIcon fontSize="small" />}
            </button>
            <button type="button" className="lt-chrono-panel__close" onClick={onClose} aria-label={t("close", "Close")}>
              <CloseIcon fontSize="small" />
            </button>
          </div>
        </header>

        <div className="lt-chrono-panel__body lt-interval__body">
          <div className={`lt-interval__dial lt-interval__dial--${state.phase}`} aria-live="polite">
            <svg viewBox="0 0 120 120" aria-hidden>
              <circle className="lt-interval__dial-bg" cx="60" cy="60" r="52" />
              <circle
                className="lt-interval__dial-arc"
                cx="60"
                cy="60"
                r="52"
                strokeDasharray={circumference}
                strokeDashoffset={circumference * (1 - Math.min(1, Math.max(0, progress)))}
              />
            </svg>
            <div className="lt-interval__dial-center">
              <span className="lt-interval__phase">{phaseLabel}</span>
              <span className="lt-interval__clock">
                {formatIntervalClock(editable && state.phase === INTERVAL_PHASES.IDLE ? state.workSec : remainingSec)}
              </span>
              <span className="lt-interval__round">
                {t("liveModeIntervalRound", "Round")} {Math.max(state.round, live ? 1 : 0)}/{state.rounds}
              </span>
            </div>
          </div>

          <div className="lt-interval__controls">
            {editable ? (
              <button type="button" className="lt-chrono-panel__btn lt-chrono-panel__btn--start lt-interval__main-btn" onClick={timer.start}>
                <PlayArrowIcon fontSize="small" />
                {state.phase === INTERVAL_PHASES.DONE
                  ? t("liveModeIntervalRestart", "Start again")
                  : t("liveModeIntervalStart", "Start")}
              </button>
            ) : state.running ? (
              <button type="button" className="lt-chrono-panel__btn lt-chrono-panel__btn--stop lt-interval__main-btn" onClick={timer.pause}>
                <PauseIcon fontSize="small" />
                {t("liveModeIntervalPause", "Pause")}
              </button>
            ) : (
              <button type="button" className="lt-chrono-panel__btn lt-chrono-panel__btn--start lt-interval__main-btn" onClick={timer.resume}>
                <PlayArrowIcon fontSize="small" />
                {t("liveModeIntervalResume", "Resume")}
              </button>
            )}
            <button type="button" className="lt-chrono-panel__btn" onClick={timer.skip} disabled={!live}>
              <SkipNextIcon fontSize="small" />
              {t("liveModeIntervalSkip", "Next phase")}
            </button>
            <button
              type="button"
              className="lt-chrono-panel__btn lt-chrono-panel__btn--reset"
              onClick={timer.reset}
              disabled={state.phase === INTERVAL_PHASES.IDLE}
              aria-label={t("liveTrainingChronoReset", "Reset")}
              title={t("liveTrainingChronoReset", "Reset")}
            >
              <RestartAltIcon fontSize="small" />
            </button>
          </div>

          <div className="lt-interval__config" aria-disabled={!editable}>
            <div className="lt-interval__presets">
              {PRESETS.map((preset) => (
                <button
                  key={preset.key}
                  type="button"
                  className={`lt-interval__preset${
                    preset.workSec === state.workSec &&
                    preset.restSec === state.restSec &&
                    preset.rounds === state.rounds
                      ? " lt-interval__preset--active"
                      : ""
                  }`}
                  disabled={!editable}
                  onClick={() => timer.configure(preset)}
                >
                  <strong>{t(`liveModeIntervalPreset_${preset.key}`, preset.key)}</strong>
                  <span>
                    {preset.rounds} × {preset.workSec}s / {preset.restSec}s
                  </span>
                </button>
              ))}
            </div>
            <div className="lt-interval__steppers">
              <Stepper
                label={t("liveModeIntervalWork", "Work")}
                value={state.workSec}
                unit="s"
                step={5}
                min={5}
                max={600}
                disabled={!editable}
                onChange={(workSec) => timer.configure({ workSec })}
              />
              <Stepper
                label={t("liveModeIntervalRest", "Rest")}
                value={state.restSec}
                unit="s"
                step={5}
                min={0}
                max={600}
                disabled={!editable}
                onChange={(restSec) => timer.configure({ restSec })}
              />
              <Stepper
                label={t("liveModeIntervalRounds", "Rounds")}
                value={state.rounds}
                step={1}
                min={1}
                max={30}
                disabled={!editable}
                onChange={(rounds) => timer.configure({ rounds })}
              />
            </div>
            <p className="lt-chrono-panel__hint">
              {t("liveModeIntervalTotal", {
                time: formatIntervalClock(totalSec),
                defaultValue: "Total {{time}}",
              })}
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(sheet, getLiveTrainingPortalRoot() || document.body);
}

export default LiveTrainingIntervalTimerPanel;
